import type { TPhrases } from '@semios/app-platform-i18n-core'
import {
  VIEW_PESTS_DEGREE_DAYS_ID_insectId,
  VIEW_PESTS_PHENOLOGY_ID_insectId,
  VIEW_PESTS_TRAP_CATCHES_ID_insectId,
} from '@semios/app-platform-value-type-definitions'
import { DashboardChart, type TDashboardChartHandle } from 'App/dashboards/_components/DashboardChart'
import { DashboardDateRangePicker } from 'App/dashboards/_components/DashboardDateRangePicker'
import { DashboardMessage } from 'App/dashboards/_components/DashboardMessage'
import { DashboardShell } from 'App/dashboards/_components/DashboardShell'
import { HistoricalTrapCounts } from 'App/dashboards/_components/HistoricalTrapCounts'
import { WidgetCard } from 'App/dashboards/_components/WidgetCard'
import { buildPestTimingChartOptions } from 'App/dashboards/_utils/buildPestTimingChartOptions'
import { getBlocksForProperty } from 'App/dashboards/_utils/getBlocksForProperty'
import { getWeatherStationsForProperty } from 'App/dashboards/_utils/getWeatherStationsForProperty'
import type { TPestDashboardConfig } from 'App/dashboards/_utils/pestDashboardConfig'
import { useDashboardPreferences } from 'App/dashboards/_utils/useDashboardPreferences'
import { useDashboardValues } from 'App/dashboards/_utils/useDashboardValues'
import { useSprayRecords } from 'App/dashboards/_utils/useSprayRecords'
import { translate } from 'i18n/i18n'
import { useCallback, useMemo, useRef, useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { checkAuthorization } from 'utils/checkAuthorization'

export const PestDashboard = ({ config }: { config: TPestDashboardConfig }) => {
  const { id: dashboardId, insectId, name, layers, customSemiosWindows } = config

  // Per-dashboard property + date range, persisted to ionic storage. Landing resolves the saved
  // preference → the map's property (if valid for this pest) → first option; user changes are saved.
  const { options, selectedPropertyIds, onPropertyChange, dateFrom, dateTo, onDateRangeChange } =
    useDashboardPreferences({ dashboardId, insectId })

  const propertyId = selectedPropertyIds.length > 0 ? Number(selectedPropertyIds[0]) : null
  const properties = fieldAssetStore.useSelector((s) => s.properties)
  const propertyName = propertyId !== null ? properties?.[propertyId]?.propertyName : undefined
  // Translated pest name (config.name is the English insect name, which is the dbInsectName key).
  const pestName = translate.phrases.dbInsectName(name as TPhrases['dbInsectName'])

  // Blocks drive the bloom markers and the spray-record filter (degree days are now points-scoped).
  const blocks = useMemo(
    () => getBlocksForProperty({ properties, propertyId, insectId }),
    [properties, propertyId, insectId],
  )

  const blockIds = useMemo(() => blocks.map((b) => b.blockId), [blocks])

  // Degree days are requested at the points scope — one line per weather station. A point advertises
  // the DD value type only when it has air-temperature-in + permission, so this is exactly the
  // property's weather stations (traps/sensors are excluded).
  const weatherStations = useMemo(
    () => getWeatherStationsForProperty({ properties, propertyId, insectId }),
    [properties, propertyId, insectId],
  )

  const stationLngLats = useMemo(() => weatherStations.map((s) => s.lngLat), [weatherStations])
  // Cultivar id→name map for labeling Hull Split markers. Cultivar IDs are globally unique, so we
  // flatten every crop's cultivars rather than assuming a specific crop id.
  const crops = fieldAssetStore.useSelector((s) => s.crops)

  const cultivarNamesById = useMemo(() => {
    const map: Record<number, string> = {}

    Object.values(crops ?? {}).forEach((crop) => {
      Object.entries(crop.cultivars ?? {}).forEach(([id, cultivarName]) => {
        map[Number(id)] = cultivarName
      })
    })

    return map
  }, [crops])

  // Everything we render is permission-gated per insect + property. Trap catches also depend on
  // the pest having traps (config flag); degree days show whenever permitted.
  // Trap-catch permission is split from the layer flag so the trap table can always render its header
  // (showing an access / no-data message when empty, matching the platform's section pattern) instead
  // of vanishing. The layer flag still gates whether this pest has a trap table at all.
  const hasTrapPermission =
    propertyId !== null &&
    checkAuthorization({ permission: VIEW_PESTS_TRAP_CATCHES_ID_insectId(insectId), entity: propertyId })

  const showTrapCatches = layers.trapCatches && hasTrapPermission

  const showDegreeDays =
    propertyId !== null &&
    checkAuthorization({ permission: VIEW_PESTS_DEGREE_DAYS_ID_insectId(insectId), entity: propertyId })

  // Spray windows come from insectPhenology_<id>, which is permission-gated per insect. Only
  // request/render them when the dashboard enables the layer AND the property is permitted.
  const sprayWindowsEnabled =
    layers.sprayWindows &&
    propertyId !== null &&
    checkAuthorization({ permission: VIEW_PESTS_PHENOLOGY_ID_insectId(insectId), entity: propertyId })

  // Crop events are per-event permission-gated (bloom and hull split are separate permissions).
  const bloomEnabled =
    layers.cropEvents.includes('almondBloom') &&
    propertyId !== null &&
    checkAuthorization({ permission: 'VIEW_BLOOM_PREDICTION', entity: propertyId })

  const hullSplitEnabled =
    layers.cropEvents.includes('almondHullSplit') &&
    propertyId !== null &&
    checkAuthorization({ permission: 'VIEW_ALMOND_HULL_SPLIT_SUMMARY', entity: propertyId })

  // One refresh token shared by every widget's fetch, so the header Refresh button refetches the
  // whole dashboard (chart + sprays + trap-counts table), not just the chart.
  const [refreshToken, setRefreshToken] = useState(0)

  const { data, loading, error, lastFetchedAt, refresh } = useDashboardValues({
    propertyId,
    blockIds,
    stationLngLats,
    insectId,
    dateFrom,
    dateTo,
    includeTrapCatches: showTrapCatches,
    includeDegreeDays: showDegreeDays,
    includePhenology: sprayWindowsEnabled,
    includeBloom: bloomEnabled,
    includeHullSplit: hullSplitEnabled,
  })

  // Chart data has its own refresh(); the token drives the other widgets' independent fetches.
  const refreshAll = useCallback(() => {
    refresh()

    setRefreshToken((t) => t + 1)
  }, [refresh])

  const { data: sprayRecords, error: sprayError } = useSprayRecords({
    propertyId,
    insectId,
    dateFrom,
    dateTo,
    enabled: layers.appliedSprays,
    refreshToken,
  })

  const chartOptions = useMemo(
    () =>
      buildPestTimingChartOptions({
        data,
        propertyId,
        propertyName,
        insectId,
        blocks,
        weatherStations,
        layers,
        sprayRecords,
        cultivarNamesById,
        customSemiosWindows,
        showTrapCatches,
        showDegreeDays,
      }),
    [
      data,
      propertyId,
      propertyName,
      insectId,
      blocks,
      weatherStations,
      layers,
      sprayRecords,
      cultivarNamesById,
      customSemiosWindows,
      showTrapCatches,
      showDegreeDays,
    ],
  )

  // Download the chart as a JPG via the chart's imperative handle.
  const chartHandleRef = useRef<TDashboardChartHandle>(null)
  const chartFileName = propertyName ? `${name} Pest Timing - ${propertyName}` : `${name} Pest Timing`

  const handleChartDownload = useCallback(() => {
    chartHandleRef.current?.downloadImage(`${chartFileName}.jpg`)
  }, [chartFileName])

  return (
    <DashboardShell
      title={pestName}
      lastUpdated={lastFetchedAt}
      onRefresh={refreshAll}
      propertyOptions={options}
      selectedPropertyIds={selectedPropertyIds}
      onPropertyChange={onPropertyChange}
    >
      <WidgetCard
        title={
          propertyName
            ? translate.phrases.banyanApp('{{pestName}} Pest Timing - {{propertyName}}', {
                pestName,
                propertyName,
              })
            : translate.phrases.banyanApp('{{pestName}} Pest Timing', { pestName })
        }
        loading={loading}
        // Only offer the image download once data has loaded (no point exporting an empty chart).
        onDownload={data && !loading && !error ? handleChartDownload : undefined}
        headerActions={
          <DashboardDateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={onDateRangeChange} />
        }
      >
        {error ? (
          <DashboardMessage>
            {translate.phrases.banyanApp('Unable to load data. Try refreshing.')}
          </DashboardMessage>
        ) : (
          <>
            <DashboardChart ref={chartHandleRef} chartOptions={chartOptions} />
            {/* Spray records overlay the chart, so a failed fetch leaves the chart drawn but without
                them. Say so, or a missing spray line reads as "no sprays". */}
            {sprayError && (
              <DashboardMessage>
                {translate.phrases.banyanApp('Unable to load data. Try refreshing.')}
              </DashboardMessage>
            )}
          </>
        )}
      </WidgetCard>
      {layers.trapCatches && (
        <HistoricalTrapCounts
          insectId={insectId}
          // English pest name (same source the JPG export uses) so the CSV filename matches.
          pestName={name}
          propertyId={propertyId}
          hasPermission={hasTrapPermission}
          refreshToken={refreshToken}
        />
      )}
    </DashboardShell>
  )
}
